import { For } from "solid-js";
import {
  DEFAULT_CONFIG,
  pieceDef,
  type GameState,
  type PieceTypeId,
  type PlayerId,
} from "../../core";
import { PieceIcon } from "./PieceIcon";
import { TerrainTile } from "./TerrainTile";

// Radius 3 → a 7x7 diagram, big enough to show the Camel's (3,1) leaps.
const R = 3;
const SIZE = 2 * R + 1;
const CENTER = R * SIZE + R;

type Mark = "move" | "capture";

/**
 * Compute a piece's movement footprint on a small empty board centred on the
 * piece, plus the squares it can only reach by capturing (so pawn-like
 * diagonals show as capture markers, not plain moves). Pure: it just runs the
 * piece's own movement pattern, so it stays correct as pieces change.
 */
function footprint(type: PieceTypeId, owner: PlayerId): Map<number, Mark> {
  const config = { ...DEFAULT_CONFIG, width: SIZE, height: SIZE };
  const self = { id: "self", type, owner, hasMoved: false };
  const base = new Array(SIZE * SIZE).fill(null);
  base[CENTER] = self;

  const marks = new Map<number, Mark>();
  const ctx = (board: (typeof base)[number][]) =>
    ({ state: { config, board } as unknown as GameState, from: CENTER, piece: self });

  // Reach on an empty board: plain moves (and a slider's open lines).
  for (const m of pieceDef(type).movement(ctx(base))) {
    marks.set(m.to, m.capture ? "capture" : "move");
  }
  // Capture-only squares: drop an enemy on each cell and see what becomes takeable.
  for (let sq = 0; sq < SIZE * SIZE; sq++) {
    if (sq === CENTER || marks.has(sq)) continue;
    const board = base.slice();
    board[sq] = { id: "foe", type: "pawn", owner: owner === 0 ? 1 : 0, hasMoved: true };
    for (const m of pieceDef(type).movement(ctx(board))) {
      if (m.to === sq && m.capture) marks.set(sq, "capture");
    }
  }
  return marks;
}

export function MovePattern(props: { type: PieceTypeId; owner?: PlayerId }) {
  const owner = () => props.owner ?? 0;
  const marks = () => footprint(props.type, owner());
  // Two-cell terrain (Valley/Ridge) occupies its anchor plus a partner tile, so
  // the diagram shows it spanning two squares: "h" extends one file to the
  // right, "v" one rank up (toward the top, matching the board's orientation).
  const footprintKind = () => pieceDef(props.type).footprint ?? null;
  const partner = () => {
    const fp = footprintKind();
    if (!fp) return null;
    return fp === "h" ? CENTER + 1 : CENTER + SIZE;
  };
  // Which half of the continuous glyph each terrain cell shows. The preview is
  // rank-high at the top, unflipped: for "h" the anchor is the LEFT half and
  // the partner (one file right) the RIGHT; for "v" the anchor is the BOTTOM
  // half and the partner (one rank up = visually top) the TOP.
  const terrainHalf = (sq: number): "left" | "right" | "top" | "bottom" | null => {
    const fp = footprintKind();
    if (!fp) return null;
    if (fp === "h") return sq === CENTER ? "left" : "right";
    return sq === CENTER ? "bottom" : "top";
  };
  // Render rank-high at the top so "forward" reads upward, like the board.
  const cells: number[] = [];
  for (let rank = SIZE - 1; rank >= 0; rank--) {
    for (let file = 0; file < SIZE; file++) cells.push(rank * SIZE + file);
  }

  return (
    <div class="movepattern" style={{ "--mp-cells": SIZE }} aria-hidden="true">
      <For each={cells}>
        {(sq) => {
          const file = sq % SIZE;
          const rank = Math.floor(sq / SIZE);
          const light = (file + rank) % 2 === 1;
          const mark = marks().get(sq);
          return (
            <div class="mp-cell" classList={{ light, dark: !light }}>
              {sq === CENTER || sq === partner() ? (
                terrainHalf(sq) ? (
                  <TerrainTile type={props.type} half={terrainHalf(sq)!} class="mp-terrain" />
                ) : (
                  <span class="mp-piece">
                    <PieceIcon type={props.type} owner={owner()} />
                  </span>
                )
              ) : mark === "move" ? (
                <span class="mp-dot" />
              ) : mark === "capture" ? (
                <span class="mp-ring" />
              ) : null}
            </div>
          );
        }}
      </For>
    </div>
  );
}
